/**
 * Reusable option row for DropOptionsSection.
 * Supports checked/unchecked/indeterminate cycle and "coming soon" variant.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useId } from 'react';
import { Info } from 'lucide-react';

import { Checkbox } from '@/components/ui/form';
import { Tooltip } from '@/components/ui';

import type { DropOptionValue } from './DropOptionsSection';

function toCheckedState(v: DropOptionValue | boolean): boolean | 'indeterminate' {
  if (v === true) return true;
  if (v === false) return false;
  return 'indeterminate';
}

function nextState(current: DropOptionValue | boolean, allowUnchanged: boolean): boolean | 'unchanged' {
  const isBlank = current === 'unchanged' || current === '' || current === null;
  if (isBlank) return true;
  if (current === true) return false;
  return allowUnchanged ? 'unchanged' : true;
}

interface OptionRowProps {
  value: DropOptionValue | boolean;
  onChange: (v: boolean | 'unchanged') => void;
  label: string;
  tooltip: string;
  disabled?: boolean;
  allowUnchanged?: boolean;
}

export function OptionRow({ value, onChange, label, tooltip, disabled, allowUnchanged = false }: OptionRowProps) {
  const id = useId();

  if (value === 'mixed') {
    return (
      <div className="flex items-start gap-2">
        <Checkbox id={id} checked="indeterminate" disabled className="mt-0.5" />
        <label htmlFor={id} className="flex flex-1 cursor-default items-center gap-1.5 text-xs text-muted-foreground">
          {label}
          <Tooltip content={tooltip} side="right">
            <Info className="h-3 w-3 shrink-0 text-muted-foreground" aria-label="Info" />
          </Tooltip>
          <span className="text-[10px] italic">— mixed</span>
        </label>
        <button
          type="button"
          onClick={() => onChange(false)}
          disabled={disabled}
          className="shrink-0 text-xs text-muted-foreground underline hover:text-foreground"
        >
          Clear
        </button>
      </div>
    );
  }

  const checkedState = toCheckedState(value);
  const isUnchanged = value === 'unchanged' || value === '';

  const handleChange = () => {
    const next = nextState(value, allowUnchanged);
    onChange(next === 'unchanged' ? 'unchanged' : next);
  };

  return (
    <div className="flex items-start gap-2">
      <Checkbox
        id={id}
        checked={checkedState}
        onCheckedChange={handleChange}
        disabled={disabled}
        className="mt-0.5"
      />
      <label
        htmlFor={id}
        className={`flex flex-1 cursor-pointer items-center gap-1.5 text-xs ${isUnchanged && allowUnchanged ? 'text-muted-foreground' : ''}`}
      >
        {label}
        <Tooltip content={tooltip} side="right">
          <Info className="h-3 w-3 shrink-0 text-muted-foreground" aria-label="Info" />
        </Tooltip>
        {isUnchanged && allowUnchanged && (
          <span className="text-[10px] text-muted-foreground/60 italic">— unchanged</span>
        )}
      </label>
    </div>
  );
}

export function ComingSoonRow({ label, tooltip, testId }: { label: string; tooltip: string; testId: string }) {
  return (
    <div className="flex items-start gap-2">
      <Checkbox checked={false} disabled className="mt-0.5" />
      <label className="flex flex-1 cursor-not-allowed items-center gap-1.5 text-xs text-muted-foreground">
        {label}
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground" data-testid={testId}>
          Coming soon
        </span>
        <Tooltip content={tooltip} side="right">
          <Info className="h-3 w-3 shrink-0 text-muted-foreground" aria-label="Info" />
        </Tooltip>
      </label>
    </div>
  );
}
